import { useState } from 'react';
import MessageDialog, {
  MessageDialogInterfaceProps,
} from '../message/MessageDialog';
import NoteDialog, { NoteDialogInterfaceProps } from '../NoteDialog';
import PhoneCallDialog, {
  PhoneCallDialogInterfaceProps,
} from '../PhoneCallDialog';
import ScheduledEventDialog, {
  ScheduledEventDialogInterfaceProps,
} from '../ScheduledEventDialog';
import VisitDialog, { VisitDialogInterfaceProps } from '../VisitDialog';
import { CallsFullScreenContent } from '@act/react-components/member-page/Calls/CallsFullScreenContent';
import { useFeatureFlag } from 'redux/utils/feature-flags/featureFlagHook.hook';

export interface DialogProps {
  handleClose: () => void;
  mode: 'Create' | 'Edit';
  open: boolean;
}

export type InteractionsDialogService = {
  handleDialogClose: () => void;
  createScheduledEvent: (
    interfaceProps: ScheduledEventDialogInterfaceProps
  ) => void;
  editScheduledEvent: (
    interfaceProps: ScheduledEventDialogInterfaceProps
  ) => void;
  createVisit: (interfaceProps: VisitDialogInterfaceProps) => void;
  editVisit: (interfaceProps: VisitDialogInterfaceProps) => void;
  createNote: (interfaceProps: NoteDialogInterfaceProps) => void;
  editNote: (interfaceProps: NoteDialogInterfaceProps) => void;
  createMessage: (interfaceProps: MessageDialogInterfaceProps) => void;
  editMessage: (interfaceProps: MessageDialogInterfaceProps) => void;
  createCall: (interfaceProps: PhoneCallDialogInterfaceProps) => void;
  editCall: (interfaceProps: PhoneCallDialogInterfaceProps) => void;
};

const getRawService = () => {
  if (!isGlobalDialogServiceSetUp) {
    throw new Error(
      `Cannot use useInteractionsDialog without including <InteractionsDialogRoot /> somewhere on the dom tree`
    );
  }

  return globalDialogService;
};

// If <InteractionsDialogRoot /> is mounted after a component that uses useInteractionsDialog
// we run into a race condition where globalDialogService is used before it is created causing a problem.
// To solve this we have a lazy loading version of the service who's functions throw an error before <InteractionsDialogRoot /> is mounted.
const lazyGlobalDialogService: InteractionsDialogService = {
  handleDialogClose: () => getRawService().handleDialogClose(),
  createScheduledEvent: (interfaceProps: ScheduledEventDialogInterfaceProps) =>
    getRawService().createScheduledEvent(interfaceProps),
  editScheduledEvent: (interfaceProps: ScheduledEventDialogInterfaceProps) =>
    getRawService().editScheduledEvent(interfaceProps),
  createVisit: (interfaceProps: VisitDialogInterfaceProps) =>
    getRawService().createVisit(interfaceProps),
  editVisit: (interfaceProps: VisitDialogInterfaceProps) =>
    getRawService().editVisit(interfaceProps),
  createNote: (interfaceProps: NoteDialogInterfaceProps) =>
    getRawService().createNote(interfaceProps),
  editNote: (interfaceProps: NoteDialogInterfaceProps) =>
    getRawService().editNote(interfaceProps),
  createMessage: (interfaceProps: MessageDialogInterfaceProps) =>
    getRawService().createMessage(interfaceProps),
  editMessage: (interfaceProps: MessageDialogInterfaceProps) =>
    getRawService().editMessage(interfaceProps),
  createCall: (interfaceProps: PhoneCallDialogInterfaceProps) =>
    getRawService().createCall(interfaceProps),
  editCall: (interfaceProps: PhoneCallDialogInterfaceProps) =>
    getRawService().editCall(interfaceProps),
};

let isGlobalDialogServiceSetUp = false;
let globalDialogService: InteractionsDialogService = null;

export const InteractionsDialogRoot = () => {
  const [dialogOpen, setDialogOpen] = useState<
    | 'None'
    | 'Scheduled Event'
    | 'Note'
    | 'Visit'
    | 'Text Message'
    | 'Phone Call'
    | 'Hospitalization'
  >('None');

  const [interfaceProps, setInterfaceProps] = useState(null);
  const [mode, setMode] = useState<'Create' | 'Edit'>('Edit');
  const useNewCallsDrawer = useFeatureFlag('needs-call');
  const resetState = () => {
    setMode('Edit');
    setInterfaceProps(null);
  };
  const handleDialogClose = () => {
    resetState();
    setDialogOpen('None');
  };

  const createScheduledEvent = (
    interfaceProps: ScheduledEventDialogInterfaceProps
  ) => {
    setInterfaceProps(interfaceProps);
    setMode('Create');
    setDialogOpen('Scheduled Event');
  };
  const editScheduledEvent = (
    interfaceProps: ScheduledEventDialogInterfaceProps
  ) => {
    setInterfaceProps(interfaceProps);
    setMode('Edit');
    setDialogOpen('Scheduled Event');
  };

  const createVisit = (interfaceProps: VisitDialogInterfaceProps) => {
    setInterfaceProps(interfaceProps);
    setMode('Create');
    setDialogOpen('Visit');
  };
  const editVisit = interfaceProps => {
    setInterfaceProps(interfaceProps);
    setMode('Edit');
    setDialogOpen('Visit');
  };
  const createNote = (interfaceProps: NoteDialogInterfaceProps) => {
    setInterfaceProps(interfaceProps);
    setMode('Create');
    setDialogOpen('Note');
  };
  const editNote = (interfaceProps: NoteDialogInterfaceProps) => {
    setInterfaceProps(interfaceProps);
    setMode('Edit');
    setDialogOpen('Note');
  };
  const createMessage = (interfaceProps: MessageDialogInterfaceProps) => {
    setInterfaceProps(interfaceProps);
    setMode('Create');
    setDialogOpen('Text Message');
  };
  const editMessage = (interfaceProps: MessageDialogInterfaceProps) => {
    setInterfaceProps(interfaceProps);
    setMode('Create');
    setDialogOpen('Text Message');
  };
  const createCall = (interfaceProps: PhoneCallDialogInterfaceProps) => {
    setInterfaceProps(interfaceProps);
    setMode('Create');
    setDialogOpen('Phone Call');
  };
  const editCall = (interfaceProps: PhoneCallDialogInterfaceProps) => {
    setInterfaceProps(interfaceProps);
    setMode('Edit');
    setDialogOpen('Phone Call');
  };

  isGlobalDialogServiceSetUp = true;
  globalDialogService = {
    handleDialogClose,
    createScheduledEvent,
    editScheduledEvent,
    createVisit,
    editVisit,
    createNote,
    editNote,
    createMessage,
    editMessage,
    createCall,
    editCall,
  };

  const dialogProps = {
    handleClose: handleDialogClose,
    mode: mode,
    open: true,
    ...interfaceProps,
  };

  return (
    <>
      {dialogOpen === 'Scheduled Event' && (
        <ScheduledEventDialog {...dialogProps} />
      )}
      {dialogOpen === 'Note' && <NoteDialog {...dialogProps} />}
      {dialogOpen === 'Visit' && <VisitDialog {...dialogProps} />}
      {dialogOpen === 'Text Message' && <MessageDialog {...dialogProps} />}

      {dialogOpen === 'Phone Call' &&
        (useNewCallsDrawer ? (
          <CallsFullScreenContent {...dialogProps} />
        ) : (
          <PhoneCallDialog {...dialogProps} />
        ))}
    </>
  );
};

export const useInteractionsDialog = () => {
  return lazyGlobalDialogService;
};

export default useInteractionsDialog;

export const reactFeedDialogService = lazyGlobalDialogService;
